/**
 * Big Screen / Superscreen / Corner Screen Uploader Specifications
 * 
 * Handles MCG and Marvel venue screen content uploads.
 * - MCG: Big Screen GFX & Videos
 * - Marvel: Corner Screens, Superscreens (Fullscreen, 16:9, Side Panels)
 */

// ============================================================
// SCREEN TYPE PLACEMENTS (PILLS) - Organized by Venue
// ============================================================
export const SCREEN_PLACEMENTS = {
  MCG: [
    { label: "Big Screen - GFX & Videos", placementId: "BS-GFX-VID" },
  ],
  Marvel: [
    { label: "Corner Screens - GFX & Videos", placementId: "CORNER-SCREENS" },
    { label: "Superscreen - Fullscreen/Takeovers", placementId: "SS-FULLSCREEN" },
    { label: "Superscreen - 20:9 Safe Graphics", placementId: "SS-16-9" },
    { label: "Superscreen - Side Panels", placementId: "SS-SIDE-PANELS" },
  ],
};

// ============================================================
// COMMON VIDEO SPEC (Most screens share this)
// ============================================================
const COMMON_VIDEO_SPEC = {
  allowed: true,
  extensions: ["mp4"],
  formats: ["AVC"],
  frameRate: 25,
  minDuration: 5,
  maxDuration: 180,
  audio: { mode: "detect" },
  requiresAlpha: false,
  inspectAlpha: true,
};

const COMMON_IMAGE_SPEC = {
  allowed: true,
  extensions: ["jpg", "png"],
  requiresAlpha: false,
  inspectAlpha: true,
};

// ============================================================
// UPLOADER SPECS
// ============================================================
export const UPLOADER_SPECS = {
  // MCG Big Screen
  "BS-GFX-VID": {
    label: "Big Screen - GFX & Videos",
    specId: "BS-GFX-VID",
    prefix: "BS",
    ledType: "bigscreen",
    contentGroup: "singleAsset",
    maxFileSize: 5 * 1024 * 1024 * 1024, // 5GB per file
    
    dimensions: [{ width: 1920, height: 1080 }],
    
    imageSpec: {
      ...COMMON_IMAGE_SPEC,
      width: 1920,
      height: 1080,
    },
    
    videoSpec: {
      ...COMMON_VIDEO_SPEC,
      extensions: ["mov", "mp4"],
      formats: ["ProRes", "AVC"],
      requireProRes4444: true, // ProRes must be 4444 profile — 422/HQ variants rejected
      width: 1920,
      height: 1080,
    },
  },

  // Marvel Corner Screens
  "CORNER-SCREENS": {
    label: "Corner Screens - GFX & Videos",
    specId: "CORNER-SCREENS",
    prefix: "CS",
    ledType: "cornerscreen",
    contentGroup: "singleAsset",
    maxFileSize: 10 * 1024 * 1024 * 1024, // 10GB per file
    
    dimensions: [{ width: 1920, height: 1080 }],
    
    imageSpec: {
      ...COMMON_IMAGE_SPEC,
      width: 1920,
      height: 1080,
    },
    
    videoSpec: {
      ...COMMON_VIDEO_SPEC,
      extensions: ["mov", "mp4"],
      formats: ["ProRes", "AVC"],
      requireProRes4444: true, // ProRes must be 4444 profile — 422/HQ variants rejected
      width: 1920,
      height: 1080,
    },
  },

  // Marvel Superscreen - Fullscreen/Takeovers
  "SS-FULLSCREEN": {
    label: "Superscreen - Fullscreen/Takeovers",
    specId: "SS-FULLSCREEN",
    prefix: "SS-FS",
    ledType: "superscreen-fullscreen",
    contentGroup: "singleAsset",
    maxFileSize: 10 * 1024 * 1024 * 1024, // 10GB per file
    
    dimensions: [{ width: 3840, height: 1080 }],
    
    imageSpec: {
      ...COMMON_IMAGE_SPEC,
      width: 3840,
      height: 1080,
    },
    
    videoSpec: {
      allowed: true,
      extensions: ["mov", "mp4"],
      formats: ["ProRes", "AVC"],
      requireProRes4444: true, // ProRes must be 4444 profile — 422/HQ variants rejected
      frameRate: 25,
      width: 3840,
      height: 1080,
      minDuration: 5,
      maxDuration: 480, // 8 minutes (was 180 / 3 min)
      audio: { mode: "detect" },
      requiresAlpha: false,
      inspectAlpha: true,
    },
  },

  // Marvel Superscreen - 20:9 Safe Graphics
  "SS-16-9": {
    label: "Superscreen - 20:9 Safe Graphics",
    specId: "SS-16-9",
    prefix: "SS-16",
    ledType: "superscreen-16:9",
    contentGroup: "singleAsset",
    maxFileSize: 10 * 1024 * 1024 * 1024, // 10GB per file

    dimensions: [{ width: 1920, height: 1080 }],

    imageSpec: {
      ...COMMON_IMAGE_SPEC,
      width: 1920,
      height: 1080,
    },

    videoSpec: {
      ...COMMON_VIDEO_SPEC,
      extensions: ["mov", "mp4"],
      formats: ["ProRes", "AVC"],
      requireProRes4444: true, // ProRes must be 4444 profile — 422/HQ variants rejected
      maxDuration: 480, // 8 minutes (was 180 / 3 min)
      width: 1920,
      height: 1080,
    },
  },

  // Marvel Superscreen - Side Panels
  "SS-SIDE-PANELS": {
    label: "Superscreen - Side Panels",
    specId: "SS-SIDE-PANELS",
    prefix: "SS-SP",
    ledType: "superscreen-side-panels",
    contentGroup: "singleAsset",
    
    dimensions: [{ width: 720, height: 1080 }],
    
    imageSpec: {
      ...COMMON_IMAGE_SPEC,
      width: 720,
      height: 1080,
    },
    
    videoSpec: {
      ...COMMON_VIDEO_SPEC,
      extensions: ["mov", "mp4"],
      formats: ["ProRes", "AVC"],
      requireProRes4444: true, // ProRes must be 4444 profile — 422/HQ variants rejected
      maxDuration: 480, // 8 minutes (was 180 / 3 min)
      width: 720,
      height: 1080,
    },
  },
};

// ============================================================
// HELPER: Build human-readable spec label
// ============================================================
export function buildSpecLabel(spec) {
  const parts = [];

  // Resolution
  const w = spec.videoSpec?.width || spec.imageSpec?.width;
  const h = spec.videoSpec?.height || spec.imageSpec?.height;
  if (w && h) {
    parts.push(`${w}×${h}`);
  }

  // Video
  if (spec.videoSpec) {
    if (spec.videoSpec.extensions?.includes("mov")) {
      parts.push("MOV / MP4");
    } else {
      parts.push("MP4");
    }

    if (spec.videoSpec.formats?.includes("AVC")) {
      parts.push("H.264");
    }
    if (spec.videoSpec.formats?.includes("ProRes")) {
      parts.push(spec.videoSpec.requireProRes4444 ? "ProRes 4444" : "ProRes");
    }

    if (spec.videoSpec.frameRate) {
      parts.push(`${spec.videoSpec.frameRate}fps`);
    }

    // Duration range
    if (spec.videoSpec.minDuration && spec.videoSpec.maxDuration) {
      const minLabel = formatDurationLabel(spec.videoSpec.minDuration);
      const maxLabel = formatDurationLabel(spec.videoSpec.maxDuration);
      parts.push(`Duration: ${minLabel} - ${maxLabel}`);
    }
  }

  // Image
  if (spec.imageSpec?.allowed) {
    parts.push("Static JPG / PNG");
    parts.push("RGB");
  }

  return parts.join(" · ");
}

// ============================================================
// HELPER: Format duration for display
// ============================================================
function formatDurationLabel(seconds) {
  if (typeof seconds !== "number" || isNaN(seconds)) return "";
  
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  
  if (minutes > 0) {
    return `${minutes}:${String(secs).padStart(2, "0")}`;
  }
  return `${secs}s`;
}

// ============================================================
// HELPER: Get placements for venue
// ============================================================
export function getPlacementsForVenue(venue) {
  return SCREEN_PLACEMENTS[venue] || [];
}

const bigScreenSpecs = {
  SCREEN_PLACEMENTS,
  UPLOADER_SPECS,
  buildSpecLabel,
  getPlacementsForVenue,
};

export default bigScreenSpecs;
